export type DisplayValueType =
    | 'minutes'
    | 'calls'
    | 'chatturns'
    | 'chats'
    | 'smsparts'
    | 'sms'
    | null;

type BarConfig = {
    dataKey: string;
    fill: string;
    stackId: string;
    name: string;
};

export type SourceType = 'calls' | 'chats' | 'sms' | null;

export const isValidSource = (source: string | null): source is SourceType => {
    return (
        source === 'calls' ||
        source === 'chats' ||
        source === 'sms' ||
        source === null
    );
};

export const getTranslation = (
    source: SourceType,
    key: 'defaultCategory' | 'srcUnit',
    translationMap: Partial<
        Record<
            Exclude<SourceType, null>, // Exclude null from keys in the map
            { parentTabName: string; defaultCategory: string; srcUnit: string }
        >
    >,
): string | null => {
    if (source === null) {
        return null;
    }

    if (isValidSource(source)) {
        const translation = translationMap[source];
        if (translation) {
            return translation[key];
        } else {
            throw new Error(`No translation available for source: ${source}`);
        }
    } else {
        throw new Error(`Invalid source: ${source}`);
    }
};

export function getFirstTrue(
    sourceMap: Record<Exclude<SourceType, null>, boolean>,
): SourceType {
    for (const key of Object.keys(sourceMap) as Array<
        Exclude<SourceType, null>
    >) {
        if (sourceMap[key]) {
            return key;
        }
    }
    return null;
}

export const getDisplayValue = (
    source: SourceType,
    key: 'defaultCategory' | 'srcUnit',
    displayMap: Partial<
        Record<
            Exclude<SourceType, null>,
            { defaultCategory: DisplayValueType; srcUnit: DisplayValueType }
        >
    >,
): DisplayValueType => {
    if (source === null) {
        return null;
    }

    if (isValidSource(source)) {
        const entry = displayMap[source];
        if (entry) {
            return entry[key];
        } else {
            throw new Error(`No display value found for source: ${source}`);
        }
    } else {
        throw new Error(`Invalid source: ${source}`);
    }
};

export function getEventName(source: SourceType): string {
    switch (source) {
        case 'calls':
            return 'call.duration';
        case 'chats':
            return 'chat.turn';
        case 'sms':
            return 'sms.part';
        case null:
            return 'default.event'; // Default for null source
        default:
            throw new Error(`Invalid source: ${source}`);
    }
}

export const barConfigs: Partial<
    Record<
        Exclude<DisplayValueType, null>, // Exclude null from keys
        BarConfig[]
    >
> = {
    minutes: [
        {
            dataKey: 'outUnits',
            fill: 'url(#primaryGr)',
            stackId: 'minutes',
            name: 'minutes_outbound',
        },
        {
            dataKey: 'inUnits',
            fill: 'url(#secondaryGr)',
            stackId: 'minutes',
            name: 'minutes_inbound',
        },
    ],
    calls: [
        {
            dataKey: 'outEvents',
            fill: 'url(#primaryGr)',
            stackId: 'calls',
            name: 'calls_outbound',
        },
        {
            dataKey: 'inEvents',
            fill: 'url(#secondaryGr)',
            stackId: 'calls',
            name: 'calls_inbound',
        },
    ],
    chatturns: [
        {
            dataKey: 'inUnits',
            fill: 'url(#secondaryGr)',
            stackId: 'chatturns',
            name: 'chat_turns',
        },
    ],
    chats: [
        {
            dataKey: 'inEvents',
            fill: 'url(#secondaryGr)',
            stackId: 'chats',
            name: 'chats',
        },
    ],
    smsparts: [
        {
            dataKey: 'inUnits',
            fill: 'url(#secondaryGr)',
            stackId: 'smsparts',
            name: 'sms_parts',
        },
    ],
    sms: [
        {
            dataKey: 'inEvents',
            fill: 'url(#secondaryGr)',
            stackId: 'sms',
            name: 'sms',
        },
    ],
};
